import { width } from 'dom-helpers';
import React, { Component, CSSProperties, Fragment, useRef } from 'react';
import {
  GetEventData,
  GetHandleProps,
  GetTrackProps,
  SliderItem,
} from 'react-compound-slider';
import _cssVars from 'styles/shared.module.scss';

// *******************************************************
// RAIL
// *******************************************************
const railOuterStyle: CSSProperties = {
  position: 'absolute',
  width: '100%',
  height: _cssVars.handleSize,
  transform: 'translate(0%, -50%)',
  borderRadius: 7,
  cursor: 'pointer',
};

const railInnerStyle: CSSProperties = {
  position: 'absolute',
  width: '100%',
  height: _cssVars.railSize,
  transform: 'translate(0%, -50%)',
  borderRadius: 7,
  pointerEvents: 'none',
  backgroundColor: _cssVars.rail,
  zIndex: 1,
};

interface SliderRailPropTypes {
  activeHandleID: string;
  getEventData: GetEventData;
  getRailProps: (params?: object) => void;
  getDisplayValue?: (
    value: number,
    percent: number,
  ) => string | null | undefined;
}

interface SliderRailState {
  percent: number;
  tooltipVal: string | null | undefined;
}

export const SliderRail: React.FC<SliderRailPropTypes> = ({ getRailProps }) => {
  return (
    <Fragment>
      <div style={railOuterStyle} {...getRailProps()} />
      <div style={railInnerStyle} />
    </Fragment>
  );
};

export class SliderRailTooltip extends Component<
  SliderRailPropTypes,
  SliderRailState
> {
  state = {
    percent: 0,
    tooltipVal: undefined,
  };

  onMouseEnter = () => {
    document.addEventListener('mousemove', this.onMouseMove);
  };

  onMouseLeave = () => {
    this.setState({ tooltipVal: null });
    document.removeEventListener('mousemove', this.onMouseMove);
  };

  onMouseMove = e => {
    const {
      props: { activeHandleID, getEventData, getDisplayValue },
    } = this;
    if (activeHandleID) {
      this.setState({ tooltipVal: null });
    } else if (getDisplayValue) {
      const { value, percent } = getEventData(e);
      this.setState({
        percent,
        tooltipVal: getDisplayValue(value, percent),
      });
    }
  };

  render() {
    const {
      props: { getRailProps },
      state: { percent },
    } = this;
    const displayStyle: CSSProperties = {
      left: `${percent}%`,
      position: 'absolute',
      marginLeft: '-1.1rem',
      marginTop: '-1.875rem',
    };

    if (percent < 2) {
      displayStyle.marginLeft = '-0.3rem';
    }
    return (
      <>
        <div
          className="rail-tooltip"
          style={{
            ...railOuterStyle,
            zIndex: 10,
          }}
          {...getRailProps({
            onMouseEnter: this.onMouseEnter,
            onMouseLeave: this.onMouseLeave,
          })}
        />
      </>
    );
  }
}

// *******************************************************
// HANDLE COMPONENT
// *******************************************************
interface HandlePropTypes {
  domain: ReadonlyArray<number>;
  handle: { id: string; value: number; percent: number };
  disabled?: boolean;
  getHandleProps: GetHandleProps;
  getDisplayValue?: (value: number, percent: number) => string;
}

export const Handle: React.FC<
  HandlePropTypes & {
    values: ReadonlyArray<number>;
  }
> = ({
  domain: [min, max],
  handle: { id, value, percent },
  getHandleProps,
  //   getDisplayValue,
  //   values: [valMin, valMax],
}) => {
  const displayStyle: CSSProperties = {
    left: `${percent}%`,
  };
  if (id === '$$-0') {
    displayStyle.marginLeft = '-0.3rem';
  }
  return (
    <Fragment>
      <div
        style={{
          left: `${percent}%`,
          position: 'absolute',
          transform: 'translate(-50%, -50%)',
          WebkitTapHighlightColor: 'rgba(0,0,0,0)',
          zIndex: 5,
          width: 28,
          height: 42,
          cursor: 'pointer',
          // border: '1px solid white',
          backgroundColor: 'none',
        }}
        {...getHandleProps(id)}
      />
      <div
        role="slider"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        style={{
          left: `${percent}%`,
          position: 'absolute',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          width: _cssVars.handleSize,
          height: _cssVars.handleSize,
          borderRadius: '50%',
          // boxShadow: '1px 1px 1px 1px rgba(0, 0, 0, 0.3)',
          backgroundColor: _cssVars.handle,
        }}
      />
    </Fragment>
  );
};

Handle.defaultProps = {
  disabled: false,
};

// *******************************************************
// KEYBOARD HANDLE COMPONENT
// Uses a button to allow keyboard events
// *******************************************************
export const KeyboardHandle: React.FC<HandlePropTypes> = ({
  handle: { id, percent },
  getHandleProps,
}) => {
  return (
    <button
      style={{
        left: `${percent}%`,
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        zIndex: 2,
        width: _cssVars.handleSize,
        height: _cssVars.handleSize,
        borderRadius: '50%',
        // boxShadow: '1px 1px 1px 1px rgba(0, 0, 0, 0.3)',
        backgroundColor: _cssVars.handle,
      }}
      {...getHandleProps(id)}
    />
  );
};

KeyboardHandle.defaultProps = {
  disabled: false,
};

// *******************************************************
// TRACK COMPONENT
// *******************************************************
interface TrackPropTypes {
  getTrackProps: GetTrackProps;
  disabled?: boolean;
  source: SliderItem;
  target: SliderItem;
  getDisplayValue?: (val: number, percent: number) => string;
}

export const Track: React.FC<TrackPropTypes> = ({
  source,
  target,
  getTrackProps,
  //   children,
  getDisplayValue,
}) => {
  let styles: CSSProperties = {
    position: 'absolute',
    transform: 'translate(0%, -50%)',
    height: _cssVars.trackSize,
    zIndex: 1,
    backgroundColor: _cssVars.track,
    borderRadius: 7,
    cursor: 'pointer',
    left: `${source.percent}%`,
    width: `${target.percent - source.percent}%`,
  };

  const parentRef = useRef(null);
  const firstRef = useRef(null);
  const secondRef = useRef(null);

  if (
    getDisplayValue &&
    secondRef.current &&
    firstRef.current &&
    parentRef.current
  ) {
    styles = {
      transform: 'translate(0%, -50%)',
      position: 'absolute',
      left: `${source.percent}%`,
      width: `${target.percent - source.percent}%`,
    };

    const fW = width(firstRef.current as HTMLElement);
    const sW = width(secondRef.current as HTMLElement);
    const totalW = width(parentRef.current as HTMLElement);
    const maxWidth = sW + fW;
    const maxPercent = Math.round((maxWidth * 100) / totalW);
    const maxPercentToRight = Math.round(100 - maxPercent);
    if (source.percent > maxPercentToRight) {
      const { left, ...newStyle } = styles;
      newStyle.width = `${maxWidth}px`;
      newStyle.right = '0';
      styles = newStyle;
    }
  }

  return (
    <div ref={parentRef} style={{ position: 'relative', width: '100%' }}>
      <div style={styles} {...getTrackProps()}>
        {getDisplayValue ? (
          <div className="time-label-wrap">
            <span ref={firstRef} className="time-label time-label--first">
              {getDisplayValue(source.value, source.percent)}
            </span>
            <span ref={secondRef} className="time-label time-label--second">
              {getDisplayValue(target.value, target.percent)}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

Track.defaultProps = {
  disabled: false,
};
